import React, { useState, useContext, useEffect, useCallback, useMemo } from 'react';
import { Navigate, useSearchParams } from 'react-router-dom';
import Box from '@mui/material/Box';
import Stack from '@mui/material/Stack';
import Grid from '@mui/material/Grid';
import Paper from '@mui/material/Paper';
import Typography from '@mui/material/Typography';
import Button from '@mui/material/Button';
import Tabs from '@mui/material/Tabs';
import Tab from '@mui/material/Tab';
import AssessmentOutlinedIcon from '@mui/icons-material/AssessmentOutlined';
import FileDownloadOutlinedIcon from '@mui/icons-material/FileDownloadOutlined';
import api from '../services/api';
import { AuthContext } from '../contexts/AuthContext';
import PageContainer from '../components/ui/PageContainer';
import StatusChip from '../components/ui/StatusChip';
import PremiumSelect from '../components/PremiumSelect';
import ReportsIndividualFeedbackHistory from '../components/ReportsIndividualFeedbackHistory';
import TimeclockReportsPanel from '../components/TimeclockReportsPanel';
import { User } from '../types';

type ReportsSection = 'feedback' | 'timeclock';

interface ReportData {
  averageScore?: string | number;
  teamAverage?: string | number;
  totalFeedbacks?: number;
  feedbacks?: any[];
  teamStats?: any[];
}

type ReportKind = 'individual' | 'team';

interface ReportShellProps {
  sectionKicker: string;
  sectionTitle: string;
  sectionLead?: string;
  categoryTabs?: React.ReactNode;
  children: React.ReactNode;
}

const ReportsCategoryTabs: React.FC<{
  section: ReportsSection;
  onSectionChange: (section: ReportsSection) => void;
}> = ({ section, onSectionChange }) => (
  <Box sx={{ borderBottom: 1, borderColor: 'divider', mb: 3 }}>
    <Tabs
      value={section}
      onChange={(_event, value: ReportsSection) => onSectionChange(value)}
      variant="scrollable"
      scrollButtons="auto"
      aria-label="Categoria de relatório"
    >
      <Tab value="feedback" label="Feedbacks" />
      <Tab value="timeclock" label="Ponto eletrônico" />
    </Tabs>
  </Box>
);

const ReportShell: React.FC<ReportShellProps> = ({
  sectionKicker,
  sectionTitle,
  sectionLead,
  categoryTabs,
  children,
}) => (
  <PageContainer
    icon={<AssessmentOutlinedIcon />}
    title="Relatórios"
    subtitle="Exporte relatórios de feedbacks e de ponto eletrônico (banco de horas e batidas) conforme o seu papel na plataforma."
  >
    <Box sx={{ mb: 2 }}>
      <StatusChip label="Indicadores" tone="accent" />
    </Box>

    {categoryTabs}

    <Paper sx={{ p: { xs: 2, md: 3 }, border: 1, borderColor: 'divider' }}>
      <Box sx={{ mb: 2 }}>
        <Typography
          variant="overline"
          sx={{ color: 'text.secondary', fontWeight: 700, letterSpacing: '0.08em' }}
        >
          {sectionKicker}
        </Typography>
        <Typography variant="h5" component="h2" sx={{ fontWeight: 800 }}>
          {sectionTitle}
        </Typography>
        {sectionLead ? (
          <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
            {sectionLead}
          </Typography>
        ) : null}
      </Box>
      {children}
    </Paper>
  </PageContainer>
);

const formatScore = (value: string | number | undefined): string => {
  if (value === undefined || value === null) {
    return '—';
  }
  if (typeof value === 'number') {
    return Number.isFinite(value) ? value.toFixed(2) : '—';
  }
  return String(value);
};

const Reports: React.FC = () => {
  const authContext = useContext(AuthContext);
  const { user } = authContext || {};
  const [searchParams, setSearchParams] = useSearchParams();
  const reportsSection: ReportsSection =
    searchParams.get('section') === 'timeclock' ? 'timeclock' : 'feedback';
  const setReportsSection = useCallback(
    (section: ReportsSection): void => {
      if (section === 'timeclock') setSearchParams({ section: 'timeclock' });
      else setSearchParams({});
    },
    [setSearchParams],
  );
  const canAccessTimeclockReports =
    user?.role === 'admin' || user?.role === 'hr' || user?.role === 'manager';
  const categoryTabs = canAccessTimeclockReports ? (
    <ReportsCategoryTabs section={reportsSection} onSectionChange={setReportsSection} />
  ) : null;

  const [reportData, setReportData] = useState<ReportData | null>(null);
  const [loading, setLoading] = useState<boolean>(false);
  const [directoryUsers, setDirectoryUsers] = useState<User[]>([]);
  const [reportKind, setReportKind] = useState<ReportKind>('individual');
  const [selectedTargetId, setSelectedTargetId] = useState<string>('');
  const [managerViewMode, setManagerViewMode] = useState<'team' | 'individual'>('individual');
  const [managerTeamMembers, setManagerTeamMembers] = useState<User[]>([]);
  const [managerSelectedDeveloperId, setManagerSelectedDeveloperId] = useState<string>('');

  const loadDirectoryUsers = useCallback(async (): Promise<void> => {
    try {
      const response = await api.get<User[]>('/users');
      setDirectoryUsers((response.data || []).filter((u) => u.active));
    } catch (error) {
      console.error('Erro ao carregar usuários para relatórios:', error);
    }
  }, []);

  useEffect(() => {
    if (user?.role === 'admin' || user?.role === 'hr') {
      loadDirectoryUsers();
    }
  }, [user?.role, loadDirectoryUsers]);

  const loadManagerTeam = useCallback(async (): Promise<void> => {
    try {
      const response = await api.get<User[]>('/users/team');
      setManagerTeamMembers(response.data || []);
    } catch (error) {
      console.error('Erro ao carregar time do gestor:', error);
    }
  }, []);

  useEffect(() => {
    if (user?.role === 'manager') {
      loadManagerTeam();
    }
  }, [user?.role, loadManagerTeam]);

  useEffect(() => {
    setManagerSelectedDeveloperId('');
    setReportData(null);
  }, [managerViewMode]);

  useEffect(() => {
    setSelectedTargetId('');
    setReportData(null);
  }, [reportKind]);

  const developers = useMemo(
    () => directoryUsers.filter((u) => u.role === 'developer'),
    [directoryUsers],
  );

  const managers = useMemo(
    () => directoryUsers.filter((u) => u.role === 'manager'),
    [directoryUsers],
  );

  const generateReport = async (type: string, userId: string): Promise<void> => {
    setLoading(true);
    try {
      const response = await api.get(`/reports/${type}/${userId}`);
      setReportData(response.data);
    } catch (error) {
      console.error('Erro ao gerar relatório:', error);
    } finally {
      setLoading(false);
    }
  };

  const exportReport = async (type: string, targetId: string, format: 'excel' | 'pdf'): Promise<void> => {
    try {
      const response = await api.get(`/reports/${type}/${targetId}/${format}`, {
        responseType: 'blob',
      });
      const ext = format === 'excel' ? 'xlsx' : 'pdf';
      const baseName = type === 'team' ? `relatorio-time-${targetId}` : `relatorio-${targetId}`;
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `${baseName}.${ext}`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
    } catch (error) {
      console.error('Erro ao exportar relatório:', error);
    }
  };

  if (!user) {
    return (
      <PageContainer maxWidth="sm">
        <Paper sx={{ p: { xs: 3, md: 4 }, border: 1, borderColor: 'divider' }}>
          <Box sx={{ mb: 2 }}>
            <StatusChip label="Carregando" tone="info" />
          </Box>
          <Typography variant="h5" component="h1" sx={{ fontWeight: 800 }}>
            Preparando relatórios
          </Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
            Validando seu perfil e permissões de exportação.
          </Typography>
        </Paper>
      </PageContainer>
    );
  }

  if (user.role === 'developer') {
    return <Navigate to="/" replace />;
  }

  if (user.role === 'manager') {
    const canIndividual = Boolean(managerSelectedDeveloperId);

    if (reportsSection === 'timeclock') {
      return (
        <ReportShell
          sectionKicker="Ponto eletrônico"
          sectionTitle="Relatórios de ponto"
          sectionLead="Banco de horas e log de entradas e saídas dos colaboradores sob sua responsabilidade."
          categoryTabs={categoryTabs}
        >
          <TimeclockReportsPanel />
        </ReportShell>
      );
    }

    return (
      <ReportShell
        sectionKicker="Gestão do time"
        sectionTitle="Relatórios de feedbacks"
        sectionLead="Gere visão consolidada do time ou relatório individual por cada desenvolvedor vinculado a você."
        categoryTabs={categoryTabs}
      >
        <Grid container spacing={2} sx={{ mb: 3 }}>
          <Grid size={{ xs: 12, md: 6 }}>
            <PremiumSelect
              id="manager-report-mode"
              label="Tipo de relatório"
              value={managerViewMode}
              options={[
                { value: 'individual', label: 'Individual (desenvolvedor do time)' },
                { value: 'team', label: 'Consolidado do time' },
              ]}
              onChange={(value) => setManagerViewMode(value as 'team' | 'individual')}
            />
          </Grid>
          <Grid size={{ xs: 12, md: 6 }}>
            {managerViewMode === 'individual' ? (
              <PremiumSelect
                id="manager-developer"
                label="Desenvolvedor"
                value={managerSelectedDeveloperId}
                options={managerTeamMembers.map((m) => ({
                  value: m.id,
                  label: `${m.name}${m.email ? ` — ${m.email}` : ''}`,
                }))}
                onChange={setManagerSelectedDeveloperId}
                placeholder="Selecione um membro do time..."
                required
              />
            ) : (
              <Box>
                <Typography variant="subtitle2" sx={{ fontWeight: 700 }}>
                  Escopo
                </Typography>
                <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
                  Inclui todos os feedbacks técnicos em que você é gestor responsável.
                </Typography>
              </Box>
            )}
          </Grid>
        </Grid>

        {managerViewMode === 'individual' && managerSelectedDeveloperId ? (
          <ReportsIndividualFeedbackHistory developerId={managerSelectedDeveloperId} />
        ) : null}

        <Stack direction="row" spacing={1.5} sx={{ flexWrap: 'wrap', gap: 1.5, mt: 3 }}>
          {managerViewMode === 'team' ? (
            <>
              <Button
                variant="contained"
                color="primary"
                onClick={() => user.id && generateReport('team', user.id)}
                disabled={loading}
                startIcon={<AssessmentOutlinedIcon />}
              >
                {loading ? 'Gerando...' : 'Gerar relatório'}
              </Button>
              <Button
                variant="outlined"
                onClick={() => user.id && exportReport('team', user.id, 'excel')}
                startIcon={<FileDownloadOutlinedIcon />}
              >
                Exportar Excel
              </Button>
              <Button
                variant="outlined"
                onClick={() => user.id && exportReport('team', user.id, 'pdf')}
                startIcon={<FileDownloadOutlinedIcon />}
              >
                Exportar PDF
              </Button>
            </>
          ) : (
            <>
              <Button
                variant="contained"
                color="primary"
                onClick={() => canIndividual && generateReport('individual', managerSelectedDeveloperId)}
                disabled={loading || !canIndividual}
                startIcon={<AssessmentOutlinedIcon />}
              >
                {loading ? 'Gerando...' : 'Gerar relatório'}
              </Button>
              <Button
                variant="outlined"
                onClick={() => canIndividual && exportReport('individual', managerSelectedDeveloperId, 'excel')}
                disabled={!canIndividual}
                startIcon={<FileDownloadOutlinedIcon />}
              >
                Exportar Excel
              </Button>
              <Button
                variant="outlined"
                onClick={() => canIndividual && exportReport('individual', managerSelectedDeveloperId, 'pdf')}
                disabled={!canIndividual}
                startIcon={<FileDownloadOutlinedIcon />}
              >
                Exportar PDF
              </Button>
            </>
          )}
        </Stack>

        {reportData ? (
          <Paper sx={{ mt: 3, p: { xs: 2, md: 3 }, backgroundColor: 'action.hover', border: 1, borderColor: 'divider' }}>
            {managerViewMode === 'team' ? (
              <>
                <Typography variant="h6" sx={{ fontWeight: 800 }}>
                  Média do time: {formatScore(reportData.teamAverage)}
                </Typography>
                <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
                  Total de feedbacks: {reportData.totalFeedbacks || 0}
                </Typography>
              </>
            ) : (
              <>
                <Typography variant="h6" sx={{ fontWeight: 800 }}>
                  Score médio: {formatScore(reportData.averageScore)}
                </Typography>
                <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
                  Total de feedbacks: {reportData.feedbacks?.length || 0}
                </Typography>
              </>
            )}
          </Paper>
        ) : null}
      </ReportShell>
    );
  }

  if (user.role === 'admin' || user.role === 'hr') {
    const isAdmin = user.role === 'admin';
    const options = reportKind === 'individual' ? developers : managers;
    const selectLabel =
      reportKind === 'individual' ? 'Desenvolvedor (relatório individual)' : 'Gestor (relatório do time)';
    const canRun = Boolean(selectedTargetId);

    if (reportsSection === 'timeclock') {
      return (
        <ReportShell
          sectionKicker="Ponto eletrônico"
          sectionTitle="Relatórios de ponto"
          sectionLead="Banco de horas e log mensal de batidas de todos os colaboradores ativos."
          categoryTabs={categoryTabs}
        >
          <TimeclockReportsPanel />
        </ReportShell>
      );
    }

    return (
      <ReportShell
        sectionKicker={isAdmin ? 'Auditoria' : 'Consulta'}
        sectionTitle={isAdmin ? 'Relatórios de feedbacks' : 'Relatórios de feedbacks'}
        sectionLead={
          isAdmin
            ? 'Gere e exporte relatórios individuais ou por time para qualquer colaborador cadastrado.'
            : 'Visualize dados consolidados e exporte em Excel ou PDF para qualquer desenvolvedor ou time.'
        }
        categoryTabs={categoryTabs}
      >
        <Grid container spacing={2} sx={{ mb: 3 }}>
          <Grid size={{ xs: 12, md: 6 }}>
            <PremiumSelect
              id="reports-kind"
              label="Tipo de relatório"
              value={reportKind}
              options={[
                { value: 'individual', label: 'Individual (por desenvolvedor)' },
                { value: 'team', label: 'Time (por gestor)' },
              ]}
              onChange={(value) => setReportKind(value as ReportKind)}
            />
          </Grid>
          <Grid size={{ xs: 12, md: 6 }}>
            <PremiumSelect
              id="reports-target"
              label={selectLabel}
              value={selectedTargetId}
              options={options.map((u) => ({
                value: u.id,
                label: `${u.name}${u.email ? ` — ${u.email}` : ''}`,
              }))}
              onChange={setSelectedTargetId}
              placeholder="Selecione..."
              required
            />
          </Grid>
        </Grid>

        {reportKind === 'individual' && selectedTargetId ? (
          <ReportsIndividualFeedbackHistory developerId={selectedTargetId} />
        ) : null}

        <Stack direction="row" spacing={1.5} sx={{ flexWrap: 'wrap', gap: 1.5, mt: 3 }}>
          <Button
            variant="contained"
            color="primary"
            onClick={() => canRun && generateReport(reportKind, selectedTargetId)}
            disabled={loading || !canRun}
            startIcon={<AssessmentOutlinedIcon />}
          >
            {loading ? 'Gerando...' : isAdmin ? 'Gerar relatório' : 'Carregar relatório'}
          </Button>
          <Button
            variant="outlined"
            onClick={() => canRun && exportReport(reportKind, selectedTargetId, 'excel')}
            disabled={!canRun}
            startIcon={<FileDownloadOutlinedIcon />}
          >
            Exportar Excel
          </Button>
          <Button
            variant="outlined"
            onClick={() => canRun && exportReport(reportKind, selectedTargetId, 'pdf')}
            disabled={!canRun}
            startIcon={<FileDownloadOutlinedIcon />}
          >
            Exportar PDF
          </Button>
        </Stack>

        {reportData ? (
          <Paper sx={{ mt: 3, p: { xs: 2, md: 3 }, backgroundColor: 'action.hover', border: 1, borderColor: 'divider' }}>
            {reportKind === 'team' ? (
              <>
                <Typography variant="h6" sx={{ fontWeight: 800 }}>
                  Média geral do time: {formatScore(reportData.teamAverage)}
                </Typography>
                <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
                  Total de feedbacks: {reportData.totalFeedbacks ?? 0}
                </Typography>
                {Array.isArray(reportData.teamStats) && reportData.teamStats.length > 0 ? (
                  <Stack component="ul" spacing={1} sx={{ listStyle: 'none', p: 0, mt: 2 }}>
                    {reportData.teamStats.map((row: any) => (
                      <Box component="li" key={row.developer?.id || row.developer?.email}>
                        <Typography variant="body2" component="span">
                          <strong>{row.developer?.name || '—'}</strong>
                          {' '}
                          — média {formatScore(row.averageScore)} · {row.feedbacksCount ?? 0} feedback(s)
                        </Typography>
                      </Box>
                    ))}
                  </Stack>
                ) : null}
              </>
            ) : (
              <>
                <Typography variant="h6" sx={{ fontWeight: 800 }}>
                  Score médio: {formatScore(reportData.averageScore)}
                </Typography>
                <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
                  Total de feedbacks: {reportData.feedbacks?.length || 0}
                </Typography>
              </>
            )}
          </Paper>
        ) : null}
      </ReportShell>
    );
  }

  return (
    <ReportShell
      sectionKicker="Acesso"
      sectionTitle="Relatórios"
      sectionLead="Entre com um perfil autorizado para visualizar e exportar relatórios."
    >
      <Typography variant="body2" color="text.secondary">
        Este perfil não possui acesso à área de relatórios.
      </Typography>
    </ReportShell>
  );
};

export default Reports;
